export interface SacredArtpiece {
  id: string;
  filename: string;
  title: string;
  artist: string;
  year?: string;
  description: string;
  theme?: string; // e.g., "Nativity", "Passion", "Mary", "Saints", etc.
}

export const SACRED_ARTPIECES: SacredArtpiece[] = [
  {
    id: 'trinity-rublev',
    filename: 'Andrei Rublev – Trinity.jpg',
    title: 'The Holy Trinity',
    artist: 'Andrei Rublev',
    year: '1410',
    description: 'One of the most iconic religious paintings in Eastern Orthodox tradition, depicting the three angels who visited Abraham. The serene composition and soft colors symbolize the unity and divine nature of the Trinity.',
    theme: 'Trinity'
  },
  {
    id: 'mystical-nativity',
    filename: 'Botticelli – Mystical Nativity.jpg',
    title: 'The Mystical Nativity',
    artist: 'Sandro Botticelli',
    year: '1500',
    description: 'A unique Renaissance interpretation of the Nativity that combines the earthly and spiritual realms, with angels dancing above the scene. This deeply personal work reflects Botticelli\'s spiritual fervor.',
    theme: 'Nativity'
  },
  {
    id: 'calling-matthew',
    filename: 'Caravaggio – The Calling of St. Matthew.jpg',
    title: 'The Calling of St. Matthew',
    artist: 'Michelangelo Caravaggio',
    year: '1599',
    description: 'A dramatic scene showing Jesus calling Matthew from his tax collector\'s table. Caravaggio\'s use of light and shadow emphasizes the divine intervention in Matthew\'s life and his sudden transformation.',
    theme: 'Saints'
  },
  {
    id: 'entombment',
    filename: 'Caravaggio – The Entombment of Christ.jpg',
    title: 'The Entombment of Christ',
    artist: 'Michelangelo Caravaggio',
    year: '1603',
    description: 'A powerful and emotional depiction of Christ being laid in the tomb. The composition draws viewers into the grief and reverence of the moment, with Christ\'s body positioned as the focal point.',
    theme: 'Passion'
  },
  {
    id: 'incredulity-thomas',
    filename: 'Caravaggio — The Incredulity of Saint Thomas.jpg',
    title: 'The Incredulity of St. Thomas',
    artist: 'Michelangelo Caravaggio',
    year: '1601',
    description: 'The moment when Thomas places his finger in Christ\'s wound to verify His resurrection. Caravaggio captures the drama of doubt transformed into faith through physical encounter with the risen Lord.',
    theme: 'Resurrection'
  },
  {
    id: 'supper-emmaus',
    filename: 'Caravaggio — The Supper at Emmaus.jpg',
    title: 'The Supper at Emmaus',
    artist: 'Michelangelo Caravaggio',
    year: '1606',
    description: 'The moment of recognition when the risen Christ blesses the bread at Emmaus. Caravaggio\'s dramatic lighting emphasizes the shock and wonder of the disciples as they realize who is dining with them.',
    theme: 'Resurrection'
  },
  {
    id: 'taking-christ',
    filename: 'Caravaggio — The Taking of Christ.jpg',
    title: 'The Taking of Christ',
    artist: 'Michelangelo Caravaggio',
    year: '1602',
    description: 'A dynamic, emotional scene of Christ\'s arrest in the Garden of Gethsemane. The painting\'s composition pulls the viewer into the chaos and betrayal of the moment.',
    theme: 'Passion'
  },
  {
    id: 'coronation-virgin',
    filename: 'Diego Velázquez — The Coronation of the Virgin.jpg',
    title: 'The Coronation of the Virgin',
    artist: 'Diego Velázquez',
    year: '1644',
    description: 'A triumphant depiction of the Virgin Mary being crowned in heaven by the Trinity. This work celebrates Mary\'s role as Queen of Heaven and heavenly glory.',
    theme: 'Mary'
  },
  {
    id: 'agnus-dei',
    filename: 'Francisco de Zurbarán — Agnus Dei .jpg',
    title: 'Agnus Dei (Lamb of God)',
    artist: 'Francisco de Zurbarán',
    year: '1635',
    description: 'A serene and contemplative image of the sacrificial lamb, representing Christ\'s redemptive sacrifice. The white lamb stands with gentle dignity against a dark background, symbolizing purity and sacrifice.',
    theme: 'Passion'
  },
  {
    id: 'lamentation',
    filename: 'Giotto – Lamentation (Arena Chapel).jpg',
    title: 'Lamentation of Christ',
    artist: 'Giotto di Bondone',
    year: '1304',
    description: 'A groundbreaking work from the early Renaissance that depicts the mourning over Christ\'s body. The emotional intensity and human compassion shown by the grieving figures helped revolutionize religious art.',
    theme: 'Passion'
  },
  {
    id: 'st-michael',
    filename: 'Guido Reni — Saint Michael the Archangel.jpg',
    title: 'Saint Michael the Archangel',
    artist: 'Guido Reni',
    year: '1636',
    description: 'A powerful image of the warrior archangel triumphing over Satan. St. Michael represents the victory of good over evil, shown in dynamic pose with divine light illuminating his figure.',
    theme: 'Saints'
  },
  {
    id: 'last-supper',
    filename: 'Leonardo da Vinci – The Last Supper.jpg',
    title: 'The Last Supper',
    artist: 'Leonardo da Vinci',
    year: '1495',
    description: 'Perhaps the most famous religious painting in Western art, depicting Christ\'s final meal with his apostles. Leonardo captures the moment of revelation and the varied reactions of the twelve, showing individual character and emotion.',
    theme: 'Passion'
  },
  {
    id: 'immaculate-conception-murillo',
    filename: 'Murillo — Immaculate Conception.jpg',
    title: 'The Immaculate Conception',
    artist: 'Bartolomé Esteban Murillo',
    year: '1645',
    description: 'A luminous and ethereal depiction of the Virgin Mary in her Immaculate Conception. The celestial light and angels surrounding Mary emphasize her purity and special grace.',
    theme: 'Mary'
  },
  {
    id: 'holy-family-bird',
    filename: 'Murillo — The Holy Family with a Little Bird.jpg',
    title: 'The Holy Family with a Little Bird',
    artist: 'Bartolomé Esteban Murillo',
    year: '1650',
    description: 'An intimate domestic scene of the Holy Family with Mary, Joseph, and the Christ Child. The little bird symbolizes innocence and the simplicity of domestic life, creating a warm and tender religious moment.',
    theme: 'Nativity'
  },
  {
    id: 'pieta',
    filename: 'Pieta_de_Michelangelo_-_Vaticano.jpg',
    title: 'Pietà',
    artist: 'Michelangelo Buonarroti',
    year: '1498',
    description: 'One of the greatest Renaissance sculptures, depicting Mary holding the lifeless body of Christ after the crucifixion. Michelangelo captures profound grief and tender love in this marble masterpiece.',
    theme: 'Passion'
  },
  {
    id: 'sistine-madonna',
    filename: 'Raphael – Sistine Madonna.jpg',
    title: 'The Sistine Madonna',
    artist: 'Raphael',
    year: '1512',
    description: 'A serene and graceful image of the Virgin Mary descending with the Christ Child. The two cherubs at the bottom have become iconic in Western art, representing innocence and divine protection.',
    theme: 'Mary'
  },
  {
    id: 'madonna-meadow',
    filename: 'Raphael — The Madonna of the Meadow.jpg',
    title: 'The Madonna of the Meadow',
    artist: 'Raphael',
    year: '1505',
    description: 'A peaceful landscape setting for the Virgin Mary with the Christ Child and young St. John the Baptist. This work exemplifies Renaissance harmony, with the figures perfectly integrated into a serene natural landscape.',
    theme: 'Mary'
  },
  {
    id: 'christ-storm',
    filename: 'Rembrandt — Christ in the Storm on the Sea of Galilee.jpg',
    title: 'Christ in the Storm on the Sea of Galilee',
    artist: 'Rembrandt van Rijn',
    year: '1633',
    description: 'A dramatic maritime scene showing Christ calming the storm while his disciples battle against the elements. Rembrandt\'s dynamic composition and turbulent sea convey both human fear and divine power.',
    theme: 'Miracles'
  },
  {
    id: 'prodigal-son',
    filename: 'Rembrandt — The Return of the Prodigal Son.jpg',
    title: 'The Return of the Prodigal Son',
    artist: 'Rembrandt van Rijn',
    year: '1667',
    description: 'A deeply moving depiction of forgiveness and reconciliation as the prodigal son returns to his father\'s embrace. Rembrandt\'s warm lighting and compassionate portrayal emphasize God\'s mercy and unconditional love.',
    theme: 'Saints'
  },
  {
    id: 'christ-crucified',
    filename: 'Velázquez – Christ Crucified.jpg',
    title: 'Christ Crucified',
    artist: 'Diego Velázquez',
    year: '1632',
    description: 'A solemn and intimate portrayal of the crucified Christ. Velázquez\'s restrained emotional expression and precise anatomy create a meditative image of Christ\'s sacrifice.',
    theme: 'Passion'
  },
  {
    id: 'immaculate-concepcion-aranjuez',
    filename: '1280px-Inmaculada_Concepción_de_Aranjuez.jpg',
    title: 'The Immaculate Conception of Aranjuez',
    artist: 'Diego Velázquez',
    year: '1632',
    description: 'A celestial vision of the Virgin Mary surrounded by angels and divine light. This work celebrates Mary\'s purity and heavenly glory through masterful use of color and composition.',
    theme: 'Mary'
  },
  {
    id: 'coronation-virgin-velazquez',
    filename: 'Velázquez – Coronation of the Virgin.jpg',
    title: 'The Coronation of the Virgin',
    artist: 'Diego Velázquez',
    year: '1644',
    description: 'A triumphant heavenly scene of Mary\'s coronation by the Trinity. Angels celebrate as Mary assumes her role as Queen of Heaven, surrounded by divine light and glory.',
    theme: 'Mary'
  },
  {
    id: 'madonna-angels',
    filename: 'William-Adolphe Bouguereau — The Madonna of the Angels.jpg',
    title: 'The Madonna of the Angels',
    artist: 'William-Adolphe Bouguereau',
    year: '1878',
    description: 'A tender and romantic depiction of the Virgin Mary with the Christ Child, surrounded by adoring cherubs. Bouguereau\'s soft technique and idealized beauty exemplify 19th-century religious sentiment.',
    theme: 'Mary'
  }
];

export const getRandomArtpiece = (): SacredArtpiece => {
  return SACRED_ARTPIECES[Math.floor(Math.random() * SACRED_ARTPIECES.length)];
};

export const getArtpieceById = (id: string): SacredArtpiece | undefined => {
  return SACRED_ARTPIECES.find(art => art.id === id);
};

export const getArtpiecesByTheme = (theme: string): SacredArtpiece[] => {
  return SACRED_ARTPIECES.filter(art => art.theme?.toLowerCase() === theme.toLowerCase());
};
